/* ============================================================
   ENAR PHARMA — enar.js
   Hero Slider | Product Carousel | Reviews Carousel | FAQ | Scroll Animations
   ============================================================ */

(function () {
  'use strict';

  /* ---- Hero Slider ---- */
  function initHeroSlider() {
    const slider = document.querySelector('.bm-hero');
    if (!slider) return;

    const track = slider.querySelector('.bm-hero__track');
    const dots = slider.querySelectorAll('.bm-hero__dot');
    const prevBtn = slider.querySelector('.bm-hero__arrow--prev');
    const nextBtn = slider.querySelector('.bm-hero__arrow--next');
    const slides = slider.querySelectorAll('.bm-hero__slide');

    let current = 0;
    let autoTimer = null;
    const total = slides.length;

    function goTo(idx, fromSync) {
      current = (idx + total) % total;
      track.style.transform = `translateX(-${current * 100}%)`;
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
      if (!fromSync) {
        window.dispatchEvent(new CustomEvent('bm:hero-goto', { detail: { index: current, total: total } }));
      }
    }

    function next() { goTo(current + 1); }
    function prev() { goTo(current - 1); }

    function startAuto() {
      autoTimer = setInterval(next, 5200);
    }

    function resetAuto() {
      clearInterval(autoTimer);
      startAuto();
    }

    /* Two-way sync with any Mega Image/Product Slider on the page (enar-mega-slider.liquid):
       when IT advances (autoplay, arrows, dots, or swipe), this Hero follows to match.
       fromSync=true on the resulting goTo() call so it does NOT re-dispatch bm:hero-goto —
       that's what prevents an infinite back-and-forth loop between the two sliders. */
    window.addEventListener('bm:mega-goto', function (e) {
      if (e.detail && typeof e.detail.index === 'number') {
        goTo(e.detail.index, true);
        resetAuto();
      }
    });

    if (nextBtn) nextBtn.addEventListener('click', () => { next(); resetAuto(); });
    if (prevBtn) prevBtn.addEventListener('click', () => { prev(); resetAuto(); });

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { goTo(i); resetAuto(); });
    });

    // Touch support
    let touchStartX = 0;
    slider.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; }, { passive: true });
    slider.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) { diff > 0 ? next() : prev(); resetAuto(); }
    }, { passive: true });

    goTo(0);
    startAuto();
  }

  /* ---- Generic Carousel ---- */
  function initCarousel(wrapSelector, trackSelector, itemSelector, prevSelector, nextSelector) {
    document.querySelectorAll(wrapSelector).forEach(wrap => {
      const track = wrap.querySelector(trackSelector);
      const items = wrap.querySelectorAll(itemSelector);
      const prevBtn = wrap.querySelector(prevSelector);
      const nextBtn = wrap.querySelector(nextSelector);
      if (!track || items.length === 0) return;

      let current = 0;

      function getVisible() {
        const w = wrap.offsetWidth;
        if (w < 600) return 1;
        if (w < 900) return 2;
        return wrapSelector.includes('reviews') ? 3 : 4;
      }

      function getMax() {
        return Math.max(0, items.length - getVisible());
      }

      /* Hide the prev/next arrows entirely when there's nothing to
         scroll to — either because too few items were added, or
         because all of them already fit in the current viewport.
         Re-checked on resize since "how many fit" changes with width.
         Guard against the offsetWidth being 0 at first paint (common
         on mobile while images/layout settle) — if we can't measure
         yet, hide the arrows rather than leaving them stranded over
         empty space, and re-measure once layout is ready. */
      function updateArrowVisibility() {
        const measurable = wrap.offsetWidth > 0;
        const canScroll = measurable && getMax() > 0;
        if (prevBtn) prevBtn.style.display = canScroll ? '' : 'none';
        if (nextBtn) nextBtn.style.display = canScroll ? '' : 'none';
      }
      updateArrowVisibility();
      /* Re-run after the page has fully loaded (images included) and
         once more on the next animation frame, so the measurement is
         taken against the final laid-out width, not the initial one. */
      window.addEventListener('load', updateArrowVisibility);
      requestAnimationFrame(updateArrowVisibility);

      function goTo(idx) {
        current = Math.max(0, Math.min(idx, getMax()));
        const itemW = items[0].offsetWidth + 24; // gap 24px
        let offset = current * itemW;
        /* Hard safety clamp against the track's real measured width —
           this can never leave a gap of empty space after the last
           item, even if getVisible()/getMax()'s arithmetic estimate
           is ever off by one for some viewport width. */
        const maxOffset = Math.max(0, track.scrollWidth - wrap.clientWidth);
        if (offset > maxOffset) offset = maxOffset;
        track.style.transform = `translateX(-${offset}px)`;
      }

      if (prevBtn) prevBtn.addEventListener('click', () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener('click', () => goTo(current + 1));

      // Touch support
      let touchStartX = 0;
      let touchDeltaX = 0;
      track.addEventListener('touchstart', e => { touchStartX = e.touches[0].clientX; touchDeltaX = 0; }, { passive: true });
      track.addEventListener('touchmove', e => { touchDeltaX = e.touches[0].clientX - touchStartX; }, { passive: true });
      track.addEventListener('touchend', () => {
        if (Math.abs(touchDeltaX) > 40) { touchDeltaX < 0 ? goTo(current + 1) : goTo(current - 1); }
      }, { passive: true });

      window.addEventListener('resize', () => { updateArrowVisibility(); goTo(current); });
    });
  }

  /* ---- FAQ Accordion ---- */
  function initFAQ() {
    document.querySelectorAll('.bm-faq-trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const expanded = trigger.getAttribute('aria-expanded') === 'true';
        const faqItem = trigger.closest('.bm-faq-item');
        const body = faqItem.querySelector('.bm-faq-body');

        // Close others
        document.querySelectorAll('.bm-faq-item').forEach(item => {
          if (item !== faqItem) {
            item.querySelector('.bm-faq-trigger').setAttribute('aria-expanded', 'false');
            item.querySelector('.bm-faq-body').style.maxHeight = '0px';
          }
        });

        if (expanded) {
          trigger.setAttribute('aria-expanded', 'false');
          body.style.maxHeight = '0px';
        } else {
          trigger.setAttribute('aria-expanded', 'true');
          body.style.maxHeight = body.scrollHeight + 'px';
        }
      });
    });
  }

  /* ---- Scroll Reveal ---- */
  function initScrollReveal() {
    if (!('IntersectionObserver' in window)) {
      document.querySelectorAll('.bm-reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.bm-reveal').forEach(el => observer.observe(el));
  }

  /* ---- Stat Bar Animations ---- */
  function initStatBars() {
    const bars = document.querySelectorAll('.bm-stat-bar__fill');
    if (!bars.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const bar = entry.target;
          const pct = bar.dataset.pct || '90';
          bar.style.width = pct + '%';
          observer.unobserve(bar);
        }
      });
    }, { threshold: 0.3 });

    bars.forEach(bar => observer.observe(bar));
  }

  /* ---- Collection Filter Pills ---- */
  function initCollectionFilters() {
    const pills = document.querySelectorAll('.bm-filter-pill');
    const grid = document.querySelector('.bm-collection__grid');
    if (!pills.length || !grid) return;

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        pills.forEach(p => p.classList.remove('is-active'));
        pill.classList.add('is-active');

        const filter = pill.dataset.filter;
        const cards = grid.querySelectorAll('.bm-product-card');

        cards.forEach(card => {
          const tag = card.dataset.category || 'all';
          const show = filter === 'all' || tag === filter;
          card.style.display = show ? '' : 'none';
          card.style.opacity = show ? '1' : '0';
        });
      });
    });
  }

  /* ---- Newsletter Form ---- */
  function initNewsletter() {
    document.querySelectorAll('.bm-newsletter-form').forEach(form => {
      const btn = form.querySelector('.bm-newsletter-submit');
      const input = form.querySelector('.bm-newsletter-input');
      if (!btn || !input) return;

      btn.addEventListener('click', () => {
        if (!input.value || !input.value.includes('@')) {
          input.style.borderColor = 'rgba(141,187,56,0.6)';
          return;
        }
        btn.textContent = 'Subscribed! ✓';
        btn.style.background = '#28a745';
        btn.disabled = true;
        input.value = '';
      });
    });
  }

  /* ---- INIT ---- */
  function init() {
    initHeroSlider();
    initCarousel(
      '.bm-carousel-wrap',
      '.bm-carousel__track',
      '.bm-product-card',
      '.bm-carousel-arrow--prev',
      '.bm-carousel-arrow--next'
    );
    initCarousel(
      '.bm-reviews-carousel-wrap',
      '.bm-reviews-track',
      '.bm-review-card',
      '.bm-reviews-arrow--prev',
      '.bm-reviews-arrow--next'
    );
    initFAQ();
    initScrollReveal();
    initStatBars();
    initCollectionFilters();
    initNewsletter();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

/* ── Honeypot bot guard ──
   Works automatically for ANY form that renders the
   'enar-honeypot' snippet inside it — no per-form JS needed.
   Listens in the capture phase so it runs and can block the
   submission before the form's own native action/listeners fire. */
document.addEventListener('submit', function (e) {
  var form = e.target;
  if (!form || typeof form.querySelector !== 'function') return;
  var hp = form.querySelector('input[name="bm_hp_website"]');
  if (hp && hp.value.trim() !== '') {
    e.preventDefault();
    e.stopPropagation();
  }
}, true);
